import { parseAiDeckDifficulty, type AiDeckDifficulty } from '../engine/aiDeck'
import { DEFAULT_MAT_ID, isGamematId } from '../cosmetics/gamemats'
import { LEAGUE_SLEEVE_IDS } from '../cosmetics/leagueRewards'
import { LAST_CALL_SLEEVE_ID } from '../events/lastCall'

export const RANDOM_COSMETIC_ID = 'random'
export const DEFAULT_ACCENT_HEX = '#E8C547'
export const DEFAULT_MAT_OPACITY = 70
export const DEFAULT_SLEEVE_ID = 'millennium-gaze'

/** Catalog ids plus the locked Last Call sleeve. Keep aligned with client `SLEEVES`. */
const SLEEVE_IDS = new Set([
  'millennium-gaze',
  'heart-draw',
  'kingdom-isle',
  'city-skies',
  'god-triad',
  'ivory-wyrm',
  'dusk-staff',
  'carnival-ink',
  'crimson-iris',
  'five-relics',
  'classic-vortex',
  'sapphire-vortex',
  'emerald-vortex',
  'amethyst-vortex',
  'rose-vortex',
  'silver-vortex',
  LAST_CALL_SLEEVE_ID,
  ...LEAGUE_SLEEVE_IDS,
])

const HEX = /^#([0-9A-F]{6})$/

export type AppLocale = 'en' | 'es' | 'pt'
export type ColorScheme = 'light' | 'dark'
export type VsAiFirst = 'you' | 'cpu'

export type AccountPrefs = {
  locale: AppLocale
  accentHex: string
  colorScheme: ColorScheme
  vsAiDice: boolean
  vsAiFirst: VsAiFirst
  vsAiDifficulty: AiDeckDifficulty
  matId: string
  matOpacity: number
  followOpponentViewer: boolean
  focusBands: boolean
  sleeveId: string
  /** Notes that offer extra coins for playing ranked. */
  rankedPrompts: boolean
}

/**
 * Playmat preference: a catalog id or `random`.
 */
export function parseMatPref(raw: unknown): string {
  if (raw === RANDOM_COSMETIC_ID) return RANDOM_COSMETIC_ID
  if (typeof raw === 'string' && isGamematId(raw)) return raw
  return DEFAULT_MAT_ID
}

/**
 * Sleeve preference: a catalog id or `random`.
 */
export function parseSleevePref(raw: unknown): string {
  if (raw === RANDOM_COSMETIC_ID) return RANDOM_COSMETIC_ID
  if (typeof raw === 'string' && SLEEVE_IDS.has(raw)) return raw
  return DEFAULT_SLEEVE_ID
}

/** Catalog sleeve for a seat. `random` and unknown values stay unset. */
export function concreteSleeveId(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === RANDOM_COSMETIC_ID) return null
  return SLEEVE_IDS.has(raw) ? raw : null
}

/**
 * Playmat visibility 0–100.
 */
export function parseMatOpacity(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n)) return DEFAULT_MAT_OPACITY
  return Math.round(Math.min(100, Math.max(0, n)))
}

function parseLocale(raw: unknown): AppLocale {
  if (raw === 'es' || raw === 'pt') return raw
  return 'en'
}

function parseScheme(raw: unknown): ColorScheme {
  return raw === 'light' ? 'light' : 'dark'
}

function parseAccent(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_ACCENT_HEX
  const hex = raw.trim().toUpperCase()
  return HEX.test(hex) ? hex : DEFAULT_ACCENT_HEX
}

function asBool(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === false) return raw
  return fallback
}

/**
 * Defaults for a brand-new account.
 */
export function defaultAccountPrefs(): AccountPrefs {
  return {
    locale: 'en',
    accentHex: DEFAULT_ACCENT_HEX,
    colorScheme: 'dark',
    vsAiDice: true,
    vsAiFirst: 'you',
    vsAiDifficulty: 'normal',
    matId: DEFAULT_MAT_ID,
    matOpacity: DEFAULT_MAT_OPACITY,
    followOpponentViewer: true,
    focusBands: true,
    sleeveId: DEFAULT_SLEEVE_ID,
    rankedPrompts: true,
  }
}

/**
 * Coerces a PATCH/JSON body onto a full prefs object.
 */
export function parseAccountPrefs(raw: unknown): AccountPrefs {
  const base = defaultAccountPrefs()
  if (!raw || typeof raw !== 'object') return base
  const rec = raw as Record<string, unknown>
  return {
    locale: parseLocale(rec.locale),
    accentHex: parseAccent(rec.accentHex ?? rec.accent_hex),
    colorScheme: parseScheme(rec.colorScheme ?? rec.color_scheme),
    vsAiDice: asBool(rec.vsAiDice ?? rec.vs_ai_dice, base.vsAiDice),
    vsAiFirst: rec.vsAiFirst === 'cpu' || rec.vs_ai_first === 'cpu' ? 'cpu' : 'you',
    vsAiDifficulty: parseAiDeckDifficulty(
      rec.vsAiDifficulty ?? rec.vs_ai_difficulty,
    ),
    matId: parseMatPref(rec.matId ?? rec.mat_id),
    matOpacity: parseMatOpacity(rec.matOpacity ?? rec.mat_opacity),
    followOpponentViewer: asBool(
      rec.followOpponentViewer ?? rec.follow_opponent_viewer,
      base.followOpponentViewer,
    ),
    focusBands: asBool(rec.focusBands ?? rec.focus_bands, base.focusBands),
    sleeveId: parseSleevePref(rec.sleeveId ?? rec.sleeve_id),
    rankedPrompts: asBool(
      rec.rankedPrompts ?? rec.ranked_prompts,
      base.rankedPrompts,
    ),
  }
}

/**
 * Overlay of a partial PATCH onto current prefs.
 */
export function mergeAccountPrefs(
  current: AccountPrefs,
  patch: unknown,
): AccountPrefs {
  if (!patch || typeof patch !== 'object') return current
  const rec = patch as Record<string, unknown>
  const next = { ...current }
  if (rec.locale !== undefined) next.locale = parseLocale(rec.locale)
  if (rec.accentHex !== undefined || rec.accent_hex !== undefined) {
    next.accentHex = parseAccent(rec.accentHex ?? rec.accent_hex)
  }
  if (rec.colorScheme !== undefined || rec.color_scheme !== undefined) {
    next.colorScheme = parseScheme(rec.colorScheme ?? rec.color_scheme)
  }
  if (rec.vsAiDice !== undefined || rec.vs_ai_dice !== undefined) {
    next.vsAiDice = asBool(rec.vsAiDice ?? rec.vs_ai_dice, current.vsAiDice)
  }
  if (rec.vsAiFirst !== undefined || rec.vs_ai_first !== undefined) {
    const raw = rec.vsAiFirst ?? rec.vs_ai_first
    next.vsAiFirst = raw === 'cpu' ? 'cpu' : 'you'
  }
  if (rec.vsAiDifficulty !== undefined || rec.vs_ai_difficulty !== undefined) {
    next.vsAiDifficulty = parseAiDeckDifficulty(
      rec.vsAiDifficulty ?? rec.vs_ai_difficulty,
    )
  }
  if (rec.matId !== undefined || rec.mat_id !== undefined) {
    next.matId = parseMatPref(rec.matId ?? rec.mat_id)
  }
  if (rec.matOpacity !== undefined || rec.mat_opacity !== undefined) {
    next.matOpacity = parseMatOpacity(rec.matOpacity ?? rec.mat_opacity)
  }
  if (
    rec.followOpponentViewer !== undefined ||
    rec.follow_opponent_viewer !== undefined
  ) {
    next.followOpponentViewer = asBool(
      rec.followOpponentViewer ?? rec.follow_opponent_viewer,
      current.followOpponentViewer,
    )
  }
  if (rec.focusBands !== undefined || rec.focus_bands !== undefined) {
    next.focusBands = asBool(
      rec.focusBands ?? rec.focus_bands,
      current.focusBands,
    )
  }
  if (rec.sleeveId !== undefined || rec.sleeve_id !== undefined) {
    next.sleeveId = parseSleevePref(rec.sleeveId ?? rec.sleeve_id)
  }
  if (rec.rankedPrompts !== undefined || rec.ranked_prompts !== undefined) {
    next.rankedPrompts = asBool(
      rec.rankedPrompts ?? rec.ranked_prompts,
      current.rankedPrompts,
    )
  }
  return next
}
